import type { DiscoverySummary, GuideStep } from '@/lib/api/discovery';
import type { SignoffStatus } from '@/lib/api/requirements-signoff';
import type { Disposition, VendorIntelligence } from '@/lib/api/vendor-intelligence';
import { formatDate, dateValue } from '@/lib/utils/format';
import {
  dispositionMixText,
  plural,
  reportWaitsForShortlist,
  sensitivityLine,
  shortlistedProducts,
  viStepStates,
  type ReportStatus,
  type ViStep,
} from '@/components/vendor-intelligence/vi-logic';
import type { RequirementSourceCounts } from './module-status-logic';

/**
 * The graphical Overview of a Discovery and Selection engagement (5 Nov
 * 2026): what to do next, where each stage stands, and the schedule.
 *
 * Pure functions over what the API already returned: the Discovery summary
 * (its KPIs, guide counts and attention items, all counted by the server),
 * the sign-off status, the requirement counts the Selection screen holds,
 * and the vendor intelligence evaluation and report. Nothing here is a
 * rollup or a score; the only arithmetic is a difference of two counts and
 * the share a bar is filled to.
 *
 * The "Do next" list is ordered by rule, never by AI: blocked first, then
 * work to do, then what waits on someone else, then things worth a check.
 * Within "to do", the AI proposals a person must decide come first (until
 * they are, nothing they would add counts), then the work that opens the
 * next stage, in stage order.
 */

export type OverviewDestination =
  | { to: 'discovery-setup' }
  | { to: 'sessions'; review?: string; person?: string }
  | { to: 'findings'; area?: string; status?: string }
  | { to: 'maps' }
  | { to: 'signoff' }
  | { to: 'selection'; criticality?: 'not_set'; add?: 'discovery' }
  | { to: 'vendor-intelligence'; step: ViStep }
  /** Page actions: the details modal, the snapshot capture, the archive. */
  | { to: 'edit-details' }
  | { to: 'capture' }
  | { to: 'archive' };

export type ActionKind = 'blocked' | 'todo' | 'waiting' | 'check' | 'wrap';

export interface OverviewAction {
  key: string;
  kind: ActionKind;
  /** An AI proposal a person decides, or AI work under way. */
  ai: boolean;
  title: string;
  note: string | null;
  /** The button's words. */
  label: string;
  go: OverviewDestination;
}

export const ACTION_KIND_LABEL: Record<ActionKind, string> = {
  blocked: 'Blocked',
  todo: 'To do',
  waiting: 'Waiting',
  check: 'Check',
  wrap: 'Wrap up',
};

const KIND_ORDER: Record<ActionKind, number> = {
  blocked: 0,
  todo: 1,
  waiting: 2,
  check: 3,
  wrap: 4,
};

export interface OverviewInput {
  discoveryOn: boolean;
  selectionOn: boolean;
  /** Absent while it loads, or when Discovery is off. */
  summary?: DiscoverySummary | null;
  /** Read once Discovery has started. */
  signoff?: SignoffStatus | null;
  requirements?: RequirementSourceCounts | null;
  vi?: VendorIntelligence | null;
  report?: ReportStatus | null;
  plannedEnd?: string | null;
  /** Today, ISO: the caller's clock, so the rules stay pure. */
  today: string;
}

const n = (v: number) => v.toLocaleString('en-US');

function guideCounts(s: DiscoverySummary | null | undefined, key: string) {
  return s?.guide.steps.find((g: GuideStep) => g.key === key)?.counts ?? {};
}

function attentionCounts(s: DiscoverySummary | null | undefined, kind: string) {
  return s?.attention.find((a) => a.kind === kind)?.counts ?? null;
}

function published(report: ReportStatus | null | undefined): boolean {
  return (report?.versions.length ?? 0) > 0;
}

/** The interviews in the plan not yet held or given a date. */
export function sessionsToSchedule(k: DiscoverySummary['kpis']): number {
  return Math.max(0, k.interviews_total - k.interviews_held - k.interviews_scheduled);
}

/** What to do next, in the order the rule above gives. */
export function overviewActions(input: OverviewInput): OverviewAction[] {
  const out: OverviewAction[] = [];
  const add = (a: Omit<OverviewAction, 'ai'> & { ai?: boolean }) => out.push({ ai: false, ...a });
  const s = input.discoveryOn ? input.summary : null;
  const started = s?.started === true;
  const so = started ? input.signoff : null;
  const req = input.selectionOn ? input.requirements : null;
  const vi = input.selectionOn ? input.vi : null;
  const report = input.selectionOn ? input.report : null;

  // --- Blocked -----------------------------------------------------------
  if (so?.state === 'changes_requested') {
    add({
      key: 'signoff-changes',
      kind: 'blocked',
      title: 'The client asked for changes',
      note: 'Review what they asked, then request sign-off again.',
      label: 'Open sign-off',
      go: { to: 'signoff' },
    });
  } else if (so?.state === 'reopened') {
    add({
      key: 'signoff-reopened',
      kind: 'blocked',
      title: 'Sign-off reopened',
      note: 'The requirements changed after the client signed. Request it again.',
      label: 'Open sign-off',
      go: { to: 'signoff' },
    });
  }

  // --- To do: AI proposals first ------------------------------------------
  if (started && s && s.kpis.awaiting_review > 0) {
    add({
      key: 'review-evidence',
      kind: 'todo',
      ai: true,
      title: `Review ${plural(s.kpis.awaiting_review, 'evidence item')}`,
      note: 'Nothing counts until you confirm it.',
      label: 'Review',
      go: { to: 'sessions', review: 'all' },
    });
  }
  const suggestions = attentionCounts(s, 'requirement_suggestions')?.['open'] ?? 0;
  if (started && suggestions > 0) {
    add({
      key: 'review-suggestions',
      kind: 'todo',
      ai: true,
      title: `Review ${plural(suggestions, 'requirement suggestion')}`,
      note: 'Built from confirmed evidence.',
      label: 'Review',
      go: { to: 'selection', add: 'discovery' },
    });
  }
  if (vi && vi.counts.proposed > 0) {
    add({
      key: 'confirm-ratings',
      kind: 'todo',
      ai: true,
      title: `Confirm ${plural(vi.counts.proposed, 'vendor rating')}`,
      note: 'A proposed rating counts in no score.',
      label: 'Confirm',
      go: { to: 'vendor-intelligence', step: 'ratings' },
    });
  }

  // --- To do: the work that opens the next stage, in stage order ----------
  if (input.discoveryOn && s && !s.started) {
    add({
      key: 'setup-discovery',
      kind: 'todo',
      title: 'Set up Discovery',
      note: 'Choose the framework and name who you will interview.',
      label: 'Set up',
      go: { to: 'discovery-setup' },
    });
  }
  if (started && s) {
    const toSchedule = sessionsToSchedule(s.kpis);
    if (toSchedule > 0) {
      add({
        key: 'schedule-sessions',
        kind: 'todo',
        title: `Schedule ${plural(toSchedule, 'session')}`,
        note: null,
        label: 'Sessions',
        go: { to: 'sessions' },
      });
    }
    const ev = guideCounts(s, 'evidence');
    const toPlace = ev['to_place'] ?? 0;
    if (toPlace > 0) {
      add({
        key: 'place-transcripts',
        kind: 'todo',
        title: `Place ${plural(toPlace, 'transcript')}`,
        note: 'They arrived without an interview.',
        label: 'Place',
        go: { to: 'sessions' },
      });
    }
    const speakers = ev['speakers_to_match'] ?? 0;
    if (speakers > 0) {
      add({
        key: 'match-speakers',
        kind: 'todo',
        title: 'Match the speakers',
        note: `${plural(speakers, 'evidence item waits', 'evidence items wait')} until you do.`,
        label: 'Match',
        go: { to: 'sessions' },
      });
    }
    const gaps = guideCounts(s, 'close_gaps');
    const gapsOpen = s.guide.steps.find((g) => g.key === 'close_gaps')?.state !== 'locked';
    const thin = s.kpis.partial + s.kpis.none;
    if (gapsOpen && thin > 0) {
      const questions = gaps['questions'] ?? 0;
      add({
        key: 'close-gaps',
        kind: 'todo',
        title: `Cover ${plural(thin, 'more area')}`,
        note:
          questions > 0
            ? `${plural(questions, 'follow-up question')} drafted.`
            : 'Ask a follow-up or add a document.',
        label: 'Findings',
        go: { to: 'findings', area: 'all' },
      });
    }
  }
  if (req && req.total === 0) {
    add({
      key: 'load-requirements',
      kind: 'todo',
      title: 'Load requirements',
      note: 'From the library, a spreadsheet or by hand.',
      label: 'Selection',
      go: { to: 'selection' },
    });
  }
  if (req && req.notSet > 0) {
    add({
      key: 'set-criticality',
      kind: 'todo',
      title: `Set criticality on ${plural(req.notSet, 'requirement')}`,
      note: 'They carry no weight until it is set.',
      label: 'Set',
      go: { to: 'selection', criticality: 'not_set' },
    });
  }
  if (so) {
    const pains = so.checklist.find((c) => c.key === 'pain_points_decided');
    if (pains && !pains.ok) {
      add({
        key: 'decide-pain-points',
        kind: 'todo',
        title: 'Decide the pain points',
        note: 'Confirm or dismiss each one before sign-off.',
        label: 'Findings',
        go: { to: 'findings', area: 'all' },
      });
    }
    if (so.state === 'none' && so.checklist.length > 0 && so.checklist.every((c) => c.ok)) {
      add({
        key: 'request-signoff',
        kind: 'todo',
        title: 'Request sign-off',
        note: 'Every condition is met.',
        label: 'Sign-off',
        go: { to: 'signoff' },
      });
    }
  }
  if (vi && req && req.total > 0 && vi.products.length < 2) {
    add({
      key: 'add-vendors',
      kind: 'todo',
      title: 'Add vendors',
      note: 'Ratings start once two are in scope.',
      label: 'Add',
      go: { to: 'vendor-intelligence', step: 'vendors' },
    });
  }
  if (vi && vi.products.length > 0 && vi.counts.unrated > 0) {
    add({
      key: 'propose-ratings',
      kind: 'todo',
      title: `Propose ${plural(vi.counts.unrated, 'rating')}`,
      note: 'No rating yet for these vendor and module pairs.',
      label: 'Ratings',
      go: { to: 'vendor-intelligence', step: 'ratings' },
    });
  }
  const steps = vi ? viStepStates(vi, report) : null;
  if (vi && steps?.shortlist === 'todo') {
    const proposed = vi.products.filter(
      (p) => p.disposition !== null && p.disposition_status === 'proposed',
    );
    add({
      key: 'confirm-shortlist',
      kind: 'todo',
      title: 'Confirm the shortlist',
      note:
        proposed.length > 0
          ? `Proposed: ${dispositionMixText(proposed.map((p) => ({ disposition: p.disposition as Disposition })))}.`
          : null,
      label: 'Shortlist',
      go: { to: 'vendor-intelligence', step: 'shortlist' },
    });
  }
  if (steps?.report === 'todo') {
    add({
      key: report?.draft ? 'publish-report' : 'draft-report',
      kind: 'todo',
      title: report?.draft ? 'Publish the report' : 'Draft the report',
      note: report?.draft
        ? `Draft v${report.draft.version} is ready to read.`
        : 'The shortlist is confirmed.',
      label: 'Report',
      go: { to: 'vendor-intelligence', step: 'report' },
    });
  }

  // --- Waiting -------------------------------------------------------------
  if (so?.state === 'requested') {
    const sent = so.snapshot ? `Sent ${formatDate(so.snapshot.captured_at)}` : 'Sent';
    const opened = so.link?.opened_at ? `, opened ${formatDate(so.link.opened_at)}` : '';
    add({
      key: 'signoff-requested',
      kind: 'waiting',
      title: 'Client signature',
      note: `${sent}${opened}.`,
      label: 'Sign-off',
      go: { to: 'signoff' },
    });
  }
  const reading = started ? (guideCounts(s, 'evidence')['reading'] ?? 0) : 0;
  if (reading > 0) {
    add({
      key: 'transcripts-reading',
      kind: 'waiting',
      ai: true,
      title: `${plural(reading, 'transcript')} being read`,
      note: 'Their evidence comes to review when the reading ends.',
      label: 'Sessions',
      go: { to: 'sessions' },
    });
  }

  // --- Check ---------------------------------------------------------------
  const dupes = started ? (attentionCounts(s, 'possible_duplicates')?.['people'] ?? 0) : 0;
  if (dupes > 0) {
    const first = s?.attention.find((a) => a.kind === 'possible_duplicates')?.target?.person_id;
    add({
      key: 'possible-duplicates',
      kind: 'check',
      title: dupes === 1 ? '1 possible duplicate person' : `${n(dupes)} possible duplicate people`,
      note: 'Merge them so they count once.',
      label: 'Check',
      go: first ? { to: 'sessions', person: first } : { to: 'sessions' },
    });
  }
  const maps = started ? (attentionCounts(s, 'process_maps')?.['ready'] ?? 0) : 0;
  if (maps > 0) {
    add({
      key: 'processes-ready',
      kind: 'check',
      title: `${plural(maps, 'process', 'processes')} ready to map`,
      note: 'Enough confirmed evidence to draft a current-state map.',
      label: 'Maps',
      go: { to: 'maps' },
    });
  }
  if (vi && !published(report)) {
    const sens = vi.fit.sensitivity;
    const line = sensitivityLine(vi);
    if (line && sens.holds < sens.checks) {
      add({
        key: 'sensitivity',
        kind: 'check',
        title: line,
        note: 'Look before the shortlist goes in the report.',
        label: 'Check',
        go: { to: 'vendor-intelligence', step: 'shortlist' },
      });
    }
  }
  const unmapped = vi?.not_assessed?.requirements ?? 0;
  if (vi && unmapped > 0) {
    add({
      key: 'not-assessed',
      kind: 'check',
      title: `${plural(unmapped, 'requirement')} not rated`,
      note: 'They have no library module, so no vendor is rated on them.',
      label: 'Check',
      go: { to: 'vendor-intelligence', step: 'ratings' },
    });
  }
  const sched = scheduleOf(input.plannedEnd ?? null, input.today);
  if (sched.overdue && !published(report)) {
    add({
      key: 'planned-end-passed',
      kind: 'check',
      title: `Planned end passed on ${formatDate(input.plannedEnd)}`,
      note: 'Move the date, or finish and close out.',
      label: 'Edit details',
      go: { to: 'edit-details' },
    });
  }

  // --- Wrap up: only when the work is done --------------------------------
  const pending = out.some(
    (a) => a.kind === 'blocked' || a.kind === 'todo' || a.kind === 'waiting',
  );
  if (!pending && published(report)) {
    add({
      key: 'capture',
      kind: 'wrap',
      title: 'Capture a snapshot',
      note: 'Freeze the final state of the engagement.',
      label: 'Capture',
      go: { to: 'capture' },
    });
    add({
      key: 'archive',
      kind: 'wrap',
      title: 'Build the engagement archive',
      note: null,
      label: 'Build',
      go: { to: 'archive' },
    });
  }

  return out
    .map((a, i) => ({ a, i }))
    .sort((x, y) => KIND_ORDER[x.a.kind] - KIND_ORDER[y.a.kind] || x.i - y.i)
    .map(({ a }) => a);
}

// ---------------------------------------------------------------------------
// The stage track
// ---------------------------------------------------------------------------

export type StageState = 'done' | 'current' | 'todo' | 'locked';

export interface OverviewStage {
  key: 'interviews' | 'areas' | 'requirements' | 'vendors' | 'ratings' | 'shortlist' | 'report';
  group: 'discovery' | 'selection';
  name: string;
  state: StageState;
  /** The big figure; null while its read is in flight. */
  value: string | null;
  /** The words after the figure: "of 23". */
  of: string | null;
  /** Whole percent the bar is filled to; null with no bar. */
  percent: number | null;
  /** The bar and the note in the critical tone (the client sent the requirements back). */
  critical: boolean;
  note: string;
  /** Present: the note is a link to where the stage's work is done. */
  go: OverviewDestination | null;
}

function pct(part: number, whole: number): number {
  return whole > 0 ? Math.max(0, Math.min(100, Math.floor((part / whole) * 100))) : 0;
}

type StageBits = Omit<OverviewStage, 'key' | 'group' | 'name' | 'critical'> & {
  critical?: boolean;
};

const LOADING: StageBits = {
  state: 'todo',
  value: null,
  of: null,
  percent: null,
  note: '',
  go: null,
};

function locked(note: string): StageBits {
  return { state: 'locked', value: null, of: null, percent: null, note, go: null };
}

/** The stages, Discovery's two first when it is on. */
export function overviewStages(input: OverviewInput): OverviewStage[] {
  const stages: OverviewStage[] = [];
  const push = (
    key: OverviewStage['key'],
    group: OverviewStage['group'],
    name: string,
    b: StageBits,
  ) => stages.push({ key, group, name, critical: false, ...b });

  const s = input.discoveryOn ? input.summary : null;
  const started = s?.started === true;

  if (input.discoveryOn) {
    // Interviews
    if (!s) push('interviews', 'discovery', 'Interviews', LOADING);
    else if (!s.started)
      push('interviews', 'discovery', 'Interviews', {
        state: 'current',
        value: 'Not set up',
        of: null,
        percent: 0,
        note: 'Set up Discovery',
        go: { to: 'discovery-setup' },
      });
    else {
      const k = s.kpis;
      const done = k.interviews_total > 0 && k.interviews_held >= k.interviews_total;
      const toSchedule = sessionsToSchedule(k);
      push('interviews', 'discovery', 'Interviews', {
        state: done ? 'done' : 'current',
        value: n(k.interviews_held),
        of: `of ${n(k.interviews_total)}`,
        percent: pct(k.interviews_held, k.interviews_total),
        note: done
          ? 'All held'
          : k.interviews_scheduled > 0
            ? `${n(k.interviews_scheduled)} scheduled`
            : toSchedule > 0
              ? `${n(toSchedule)} to schedule`
              : 'Sessions',
        go: done ? null : { to: 'sessions' },
      });
    }

    // Areas covered
    const gapsLocked = s?.guide.steps.find((g) => g.key === 'close_gaps')?.state === 'locked';
    if (!s) push('areas', 'discovery', 'Areas covered', LOADING);
    else if (!started || gapsLocked)
      push('areas', 'discovery', 'Areas covered', locked('Opens when evidence is confirmed'));
    else {
      const k = s.kpis;
      const done = k.areas > 0 && k.corroborated >= k.areas;
      const thin = k.partial + k.none;
      push('areas', 'discovery', 'Areas covered', {
        state: done ? 'done' : 'current',
        value: n(k.corroborated),
        of: `of ${n(k.areas)}`,
        percent: pct(k.corroborated, k.areas),
        note: done ? 'All covered' : `${n(thin)} not covered yet`,
        go: done ? null : { to: 'findings', area: 'all' },
      });
    }
  }

  if (!input.selectionOn) return stages;

  // Requirements
  const req = input.requirements;
  if (!req) push('requirements', 'selection', 'Requirements', LOADING);
  else if (req.total === 0)
    push('requirements', 'selection', 'Requirements', {
      state: 'current',
      value: '0',
      of: null,
      percent: 0,
      note: 'Load requirements',
      go: { to: 'selection' },
    });
  else {
    const so = started ? input.signoff : null;
    const value = n(req.total);
    const byCriticality: StageBits = {
      state: req.notSet > 0 ? 'current' : input.discoveryOn ? 'current' : 'done',
      value,
      of: null,
      percent: pct(req.total - req.notSet, req.total),
      note: req.notSet > 0 ? `${n(req.notSet)} need a criticality` : 'All have a criticality',
      go: req.notSet > 0 ? { to: 'selection', criticality: 'not_set' } : null,
    };
    if (!so) push('requirements', 'selection', 'Requirements', byCriticality);
    else if (so.state === 'signed' || so.state === 'waived')
      push('requirements', 'selection', 'Requirements', {
        state: 'done',
        value,
        of: null,
        percent: 100,
        note:
          so.state === 'waived'
            ? 'Sign-off waived'
            : so.signed
              ? `Signed ${formatDate(so.signed.at)}`
              : 'Signed',
        go: null,
      });
    else if (so.state === 'changes_requested' || so.state === 'reopened')
      push('requirements', 'selection', 'Requirements', {
        state: 'current',
        value,
        of: null,
        percent: 100,
        critical: true,
        note: so.state === 'reopened' ? 'Sign-off reopened' : 'Changes requested',
        go: { to: 'signoff' },
      });
    else if (so.state === 'requested')
      push('requirements', 'selection', 'Requirements', {
        state: 'current',
        value,
        of: null,
        percent: 100,
        note: 'Awaiting signature',
        go: { to: 'signoff' },
      });
    else {
      const open = so.checklist.filter((c) => !c.ok).length;
      const total = so.checklist.length;
      push('requirements', 'selection', 'Requirements', {
        state: 'current',
        value,
        of: null,
        percent: pct(total - open, total),
        note:
          open > 0
            ? `${plural(open, 'sign-off condition')} open`
            : total > 0
              ? 'Ready to request sign-off'
              : byCriticality.note,
        go: { to: 'signoff' },
      });
    }
  }

  // Vendors, Ratings, Shortlist, Report: the Vendor intelligence steps.
  const vi = input.vi;
  const report = input.report;
  if (!vi) {
    if (!input.discoveryOn) push('vendors', 'selection', 'Vendors', LOADING);
    push('ratings', 'selection', 'Ratings', LOADING);
    push('shortlist', 'selection', 'Shortlist', LOADING);
    push('report', 'selection', 'Report', LOADING);
    return stages;
  }
  const st = viStepStates(vi, report);
  const products = vi.products.length;

  if (!input.discoveryOn) {
    push('vendors', 'selection', 'Vendors', {
      state: st.vendors === 'done' ? 'done' : products > 0 ? 'current' : 'todo',
      value: n(products),
      of: 'in scope',
      percent: pct(Math.min(products, 2), 2),
      note: st.vendors === 'done' ? 'Two or more in scope' : 'Add vendors',
      go: st.vendors === 'done' ? null : { to: 'vendor-intelligence', step: 'vendors' },
    });
  }

  if (st.ratings === 'locked') {
    if (input.discoveryOn && products === 0)
      push('ratings', 'selection', 'Ratings', {
        state: 'todo',
        value: 'No vendors',
        of: null,
        percent: 0,
        note: 'Add vendors',
        go: { to: 'vendor-intelligence', step: 'vendors' },
      });
    else
      push(
        'ratings',
        'selection',
        'Ratings',
        locked(
          products === 0 ? 'Opens when vendors are added' : 'Opens when requirements have modules',
        ),
      );
  } else {
    const c = vi.counts;
    const done = st.ratings === 'done';
    push('ratings', 'selection', 'Ratings', {
      state: done ? 'done' : c.confirmed > 0 || c.proposed > 0 ? 'current' : 'todo',
      value: n(c.confirmed),
      of: `of ${n(c.cells)}`,
      percent: pct(c.confirmed, c.cells),
      note: done
        ? 'All confirmed'
        : c.proposed > 0
          ? `${n(c.proposed)} to confirm`
          : `${n(c.unrated)} not rated yet`,
      go: done ? null : { to: 'vendor-intelligence', step: 'ratings' },
    });
  }

  if (st.shortlist === 'locked')
    push('shortlist', 'selection', 'Shortlist', locked('Opens when ratings rank a vendor'));
  else if (st.shortlist === 'done') {
    const alternates = vi.products.filter((p) => p.disposition === 'alternate').length;
    push('shortlist', 'selection', 'Shortlist', {
      state: 'done',
      value: n(shortlistedProducts(vi).length),
      of: 'shortlisted',
      percent: 100,
      note: alternates > 0 ? plural(alternates, 'alternate') : 'Confirmed',
      go: null,
    });
  } else {
    const proposed = vi.products.filter((p) => p.disposition === 'shortlisted').length;
    push('shortlist', 'selection', 'Shortlist', {
      state: 'current',
      value: n(proposed),
      of: 'proposed',
      percent: 0,
      note: 'Confirm the shortlist',
      go: { to: 'vendor-intelligence', step: 'shortlist' },
    });
  }

  if (st.report === 'locked')
    push(
      'report',
      'selection',
      'Report',
      locked(
        reportWaitsForShortlist(vi)
          ? 'Opens when the shortlist is confirmed'
          : 'Opens when two vendors have confirmed ratings',
      ),
    );
  else if (st.report === 'done') {
    const latest = (report?.versions ?? []).reduce((m, v) => Math.max(m, v.version), 0);
    push('report', 'selection', 'Report', {
      state: 'done',
      value: 'Published',
      of: null,
      percent: 100,
      note: report?.draft
        ? `v${latest} published · draft v${report.draft.version}`
        : `Version ${latest}`,
      go: { to: 'vendor-intelligence', step: 'report' },
    });
  } else
    push('report', 'selection', 'Report', {
      state: report?.draft ? 'current' : 'todo',
      value: report?.draft ? `Draft v${report.draft.version}` : 'Not started',
      of: null,
      percent: report?.draft ? 50 : 0,
      note: report?.draft ? 'Publish the report' : 'Draft the report',
      go: { to: 'vendor-intelligence', step: 'report' },
    });

  return stages;
}

// ---------------------------------------------------------------------------
// The schedule
// ---------------------------------------------------------------------------

export interface Schedule {
  /** Whole days from today to the planned end; negative once it has passed. */
  daysLeft: number | null;
  overdue: boolean;
  /** "238 days left", "Ends today", "11 days over", or null with no planned end. */
  text: string | null;
}

const DAY_MS = 86_400_000;

/** Days to the planned end, counted on calendar days (both ends read as local dates). */
export function scheduleOf(plannedEnd: string | null, today: string): Schedule {
  if (!plannedEnd) return { daysLeft: null, overdue: false, text: null };
  const end = dateValue(plannedEnd.slice(0, 10));
  const now = dateValue(today.slice(0, 10));
  if (Number.isNaN(end) || Number.isNaN(now)) return { daysLeft: null, overdue: false, text: null };
  const days = Math.round((end - now) / DAY_MS);
  return {
    daysLeft: days,
    overdue: days < 0,
    text:
      days > 0
        ? `${plural(days, 'day')} left`
        : days === 0
          ? 'Ends today'
          : `${plural(-days, 'day')} over`,
  };
}
